import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Container, Paper, Stack, Typography, Box,
  Button, TextField, Alert, CircularProgress, Link, Backdrop
} from '@mui/material';
import { 
  sendPasswordResetEmail,
  Auth
} from "firebase/auth";
import { auth as defaultAuth } from '../firebase';
import { logEvent } from '../analytics';

interface ForgotPasswordPageProps {
  authInstance?: Auth;
  onLoginSuccess?: () => void;
}

const getFirebaseErrorMessage = (error: any): string => {
  const code = error?.code;
  switch (code) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    default:
      return error?.message || 'Failed to send password reset email. Please try again.';
  }
};

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ 
  authInstance 
}) => {
  const auth = authInstance || defaultAuth;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  React.useEffect(() => {
    document.title = "Jets '14 | Reset Password";
  }, []);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setMessage('Password reset link sent! Please check your email inbox.');
      logEvent('forgot_password', { email });
    } catch (err: any) {
      console.error("Password reset error:", err);
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper 
        elevation={3}
        sx={{
          marginTop: { xs: 3, sm: 6 },
          marginBottom: { xs: 3, sm: 6 },
          padding: { xs: 2.5, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/chicago_jets_shield_logo.jpg'} 
            alt="Chicago Jets Logo" 
            sx={{ height: { xs: 65, sm: 80 }, width: 'auto' }} 
          />
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/csdhl_logo.png'} 
            alt="CSDHL Logo" 
            sx={{ height: { xs: 65, sm: 80 }, width: 'auto' }} 
          />
        </Stack>
        
        <Typography 
          component="h1" 
          variant="h5" 
          textAlign="center" 
          fontWeight="bold"
          sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}
        >
          Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2, mt: 0.5 }}>
          Enter your email address and we'll send you a link to reset your password.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleForgotPassword} noValidate sx={{ width: '100%' }}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="reset-email"
            label="Email Address"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            size="small"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 2, mb: 1.5, py: 1, textTransform: 'none', fontWeight: 600 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
          </Button>
          
          <Box textAlign="center" sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Remember your password?{' '}
              <Link component={RouterLink} to="/password-signin" sx={{ fontWeight: 500 }}>
                Back to Sign In
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <Link component={RouterLink} to="/" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                ← Back to Google / Email sign in
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default ForgotPasswordPage;
